import { FC, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { IconButton, Stack, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import { ShopperBadge } from './ShopperBadge';
import { TransferRequestBadge } from './TransferRequestBadge';
import { AnimatedBaseConversationCard } from './StyledConversationCards';

const StyledCloseButton = styled(IconButton)`
  position: absolute;
  top: ${({ theme }) => theme.spacing(1)};
  right: ${({ theme }) => theme.spacing(1)};
`;

const StyledAnimatedBaseConversationCard = styled(AnimatedBaseConversationCard)`
  background-color: ${({ theme }) => theme.palette.background.default};
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(3, 2)};
`;

type Props = {
  type: ConversationOutcomeCardType;
  title: string;
  subtitle: string;
  onClose?: () => void;
};

export enum ConversationOutcomeCardType {
  TRANSFER_REQUEST = 'transferRequest',
  CONVERSATION_REQUEST_NEW_SHOPPER = 'conversationRequestNewShopper',
  CONVERSATION_REQUEST_FAMILIAR_SHOPPER = 'conversationRequestFamiliarShopper',
}

const ConversationOutcomeCard: FC<Props> = ({
  type,
  title,
  subtitle,
  onClose,
}) => {
  const [isVisible, setIsVisible] = useState<boolean>(true);

  const handleClose = useCallback(() => {
    onClose?.();
    setIsVisible(false);
  }, [onClose]);

  useEffect(() => {
    const timeout = setTimeout(handleClose, 5000);
    return () => clearTimeout(timeout);

    // We only want to run this effect once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isVisible) {
    return null;
  }

  return (
    <StyledAnimatedBaseConversationCard>
      <StyledCloseButton size="small" onClick={handleClose}>
        <Close />
      </StyledCloseButton>
      {type ===
        ConversationOutcomeCardType.CONVERSATION_REQUEST_NEW_SHOPPER && (
        <ShopperBadge type="new" />
      )}
      {type ===
        ConversationOutcomeCardType.CONVERSATION_REQUEST_FAMILIAR_SHOPPER && (
        <ShopperBadge type="familiar" />
      )}
      {type === ConversationOutcomeCardType.TRANSFER_REQUEST && (
        <TransferRequestBadge />
      )}
      <Stack>
        <Typography variant="body2" fontWeight="bold">
          {title}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {subtitle}
        </Typography>
      </Stack>
    </StyledAnimatedBaseConversationCard>
  );
};

export default ConversationOutcomeCard;
